import { useContext } from 'react';
import {
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuGroupHeader,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
} from '@fluentui/react-components';
import { AddRegular } from '@fluentui/react-icons';

// Direct file import: the barrel loads the approvals components, which read user groups at module top level.
import { ControlledTooltip } from '@shared/components/ControlledTooltip';
import DocumentContext from '../../contexts/DocumentContext.context';
import useEditorActive from '../../hooks/useEditorActive.hook';
import {
  canInsertInParagraph,
  getRangeAtSelection,
  insertFormField,
  insertParagraphField,
} from '../../tiptap/nodes/formField/formFieldHelpers';
import { FORM_FIELD_ITEMS, PARAGRAPH_FIELD_ITEMS } from './constants';

type TProps = {
  className: string;
};

const AddFieldMenu = ({ className }: TProps) => {
  const { editor, templateDropdownOptionsRef } = useContext(DocumentContext);
  const isInParagraph = useEditorActive((current) => canInsertInParagraph(current.state));

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <MenuButton appearance="primary" className={className} icon={<AddRegular />}>
          Add field
        </MenuButton>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          <MenuGroup>
            <MenuGroupHeader>Form fields</MenuGroupHeader>
            {FORM_FIELD_ITEMS.map(({ fieldName, title, icon: Icon }) => (
              <MenuItem
                key={fieldName}
                icon={<Icon />}
                onClick={() =>
                  insertFormField(editor, getRangeAtSelection(editor.state), fieldName, templateDropdownOptionsRef)
                }
              >
                {title}
              </MenuItem>
            ))}
          </MenuGroup>
          <MenuDivider />
          <MenuGroup>
            <MenuGroupHeader>In this paragraph</MenuGroupHeader>
            {PARAGRAPH_FIELD_ITEMS.map(({ fieldName, title, icon: Icon }) => (
              <ControlledTooltip
                key={fieldName}
                relationship="inaccessible"
                enable={!isInParagraph}
                content="Put the caret in a paragraph's text."
                withArrow
                positioning="after"
              >
                <MenuItem
                  icon={<Icon />}
                  disabled={!isInParagraph}
                  onClick={() =>
                    insertParagraphField(editor, fieldName, templateDropdownOptionsRef, editor.state.selection.from)
                  }
                >
                  {title}
                </MenuItem>
              </ControlledTooltip>
            ))}
          </MenuGroup>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

export default AddFieldMenu;
